import { queryOptions, useMutation, useQueryClient } from "@tanstack/react-query";
import { authFetch } from "@/lib/api-client";
import { fetchAmberOptions } from "@/lib/queries/amber-options";
import type {
  CompetitorFiltersSchema,
  MyBrandFiltersSchema,
  MyBrandFilterOptionsResponse,
} from "@nexus/contracts";
import type { MyBrandKpiCards, CompetitorKpiCards, MyBrandKpiCharts, CompetitorKpiCharts } from "@nexus/contracts";
import type { z } from "zod";
import {
  bootstrapCompetitorFilterDefaults,
  fetchCompetitorFilterDefaults,
} from "@/features/dashboard/api/competitor-filter-defaults.api";
import type {
  BootstrapCompetitorFilterDefaultsPayload,
  ExploreApiPrefix,
} from "@/features/dashboard/lib/competitor-filter-defaults";
import { resolveDashboardRequestWindow } from "@/features/dashboard/lib/resolve-dashboard-request-window";
import { buildDrilldownQs } from "@/lib/queries/drilldown-query-string";
import type { DrilldownPageFiltersInput, DrilldownQuerySection } from "@/lib/queries/drilldown.queries";

// ─── Types ────────────────────────────────────────────────────────────────────

export type MyBrandFiltersInput = z.infer<typeof MyBrandFiltersSchema>;
export type CompetitorFiltersInput = z.infer<typeof CompetitorFiltersSchema>;

/** Absolute window carried on Dashboard requests — not part of the filter schemas. */
export type DashboardDateWindow = {
  startDate: string;
  endDate: string;
};

export type MyBrandDashboardFilters = MyBrandFiltersInput &
  Partial<DashboardDateWindow> & { activePresetId?: string };
export type CompetitorDashboardFilters = CompetitorFiltersInput &
  Partial<DashboardDateWindow> & { activePresetId?: string };

function appendDateWindow(qs: URLSearchParams, window: DashboardDateWindow): void {
  qs.set("startDate", window.startDate);
  qs.set("endDate", window.endDate);
}

export type MyBrandSummaryResult =
  | { status: "pending"; correlationId: string }
  | { status: "ready"; kpiCards: MyBrandKpiCards | null };

export type MyBrandChartResult =
  | { status: "pending"; correlationId: string }
  | { status: "ready"; charts: MyBrandKpiCharts | null };

export type CompetitorSummaryResult =
  | { status: "pending"; correlationId: string }
  | { status: "ready"; kpiCards: CompetitorKpiCards | null };

export type CompetitorChartResult =
  | { status: "pending"; correlationId: string }
  | { status: "ready"; charts: CompetitorKpiCharts | null };

export type DashboardCreativesResult =
  | { status: "pending"; correlationId: string }
  | { status: "ready"; section: DrilldownQuerySection | null };

/** One competitor creative row: its Drilldown sort and the window it covers. */
export interface DashboardCreativesRequest {
  sortMode: string;
  sortDirection: "asc" | "desc";
  dateRange: CompetitorFiltersInput["dateRange"];
  startDate: string;
  endDate: string;
}

export type DashboardStatusData = {
  status: "ACCEPTED" | "PROCESSING" | "COMPLETED" | "FAILED";
  kpiCards?: MyBrandKpiCards | CompetitorKpiCards | null;
  charts?: MyBrandKpiCharts | CompetitorKpiCharts | null;
  section?: DrilldownQuerySection | null;
};

// ─── Keys ─────────────────────────────────────────────────────────────────────

export const dashboardKeys = {
  all: ["dashboard"] as const,
  filterOptions: () => [...dashboardKeys.all, "my-brand-filter-options"] as const,
  myBrandFilterOptions: (tenantId: string, apiPrefix: ExploreApiPrefix) =>
    [...dashboardKeys.filterOptions(), tenantId, apiPrefix] as const,
  summaries: () => [...dashboardKeys.all, "summary"] as const,
  myBrandSummary: (tenantId: string, filters: MyBrandDashboardFilters) =>
    [...dashboardKeys.summaries(), "my-brand", tenantId, filters] as const,
  myBrandChart: (tenantId: string, filters: MyBrandDashboardFilters) =>
    [...dashboardKeys.summaries(), "my-brand-chart", tenantId, filters] as const,
  competitorSummary: (tenantId: string, filters: CompetitorDashboardFilters) =>
    [...dashboardKeys.summaries(), "competitors", tenantId, filters] as const,
  competitorChart: (tenantId: string, filters: CompetitorDashboardFilters) =>
    [...dashboardKeys.summaries(), "competitor-chart", tenantId, filters] as const,
  competitorCreatives: (
    tenantId: string,
    filters: CompetitorFiltersInput,
    request: DashboardCreativesRequest,
  ) => [...dashboardKeys.summaries(), "competitor-creatives", tenantId, filters, request] as const,
  statuses: () => [...dashboardKeys.all, "status"] as const,
  status: (correlationId: string) =>
    [...dashboardKeys.statuses(), correlationId] as const,
  competitorFilterDefaults: (tenantId: string, exploreBasePath: ExploreApiPrefix) =>
    [...dashboardKeys.all, "competitor-filter-defaults", tenantId, exploreBasePath] as const,
};

// ─── Constants ────────────────────────────────────────────────────────────────

export const POLL_INTERVAL_MS = 2000;
export const MAX_POLL_DURATION_MS = 5 * 60 * 1000;

/**
 * My Brand filter tuples. The workspace worker runs the BigQuery read, so the gateway
 * answers `200 { data }` on a warm cache and `202 { correlationId }` on a cold miss.
 *
 * There is deliberately no empty-tuples fallback: treating a pending or failed job as
 * `{ tuples: [] }` would render the filter bar as "this tenant has no data".
 */
export function myBrandFilterOptionsQueryOptions(
  tenantId: string | null,
  canFetchMyBrandOptions = true,
  apiPrefix: ExploreApiPrefix = "/dashboard",
) {
  const tid = tenantId ?? "";
  return queryOptions({
    queryKey: dashboardKeys.myBrandFilterOptions(tid || "__none__", apiPrefix),
    queryFn: ({ signal }) =>
      fetchAmberOptions<MyBrandFilterOptionsResponse>(
        `${apiPrefix}/my-brand-filter-options`,
        apiPrefix,
        { signal },
      ),
    enabled: Boolean(tid) && canFetchMyBrandOptions,
    staleTime: 1000 * 60 * 30,
    // A thrown FAILED or timeout is a settled job, not a transient 5xx — retrying it
    // just re-runs the whole poll budget.
    retry: false,
  });
}

// ─── Query options ────────────────────────────────────────────────────────────

export function dashboardSummaryQueryOptions(
  tenantId: string | null,
  filters: MyBrandDashboardFilters,
) {
  const window = resolveDashboardRequestWindow(filters);
  const keyedFilters = { ...filters, ...window };
  const qs = new URLSearchParams({ mode: "my-brand", dateRange: filters.dateRange });
  appendDateWindow(qs, window);
  if (filters.platforms.length > 0) qs.set("platforms", filters.platforms.join(","));
  if (filters.accounts.length > 0) qs.set("accounts", filters.accounts.join(","));
  if (filters.products.length > 0) qs.set("products", filters.products.join(","));

  return queryOptions<MyBrandSummaryResult | null>({
    queryKey: dashboardKeys.myBrandSummary(tenantId ?? "", keyedFilters),
    queryFn: async ({ signal }) => {
      const { data, correlationId } = await authFetch<{ kpiCards: MyBrandKpiCards }>(
        `/dashboard/summary?${qs.toString()}`,
        { signal },
      );
      if (!data) return { status: "pending", correlationId: correlationId! };
      return { status: "ready", kpiCards: data.kpiCards };
    },
    enabled: !!tenantId,
    // Dashboard data is batch-refreshed once per 24h, so there is nothing to
    // gain from background refreshes — and re-firing the async job mid-session
    // (focus/reconnect/stale) breaks the cards. Refetch only when the filter
    // queryKey changes (i.e. on filter change or first load).
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: 3,
  });
}

export function dashboardMyBrandChartQueryOptions(
  tenantId: string | null,
  filters: MyBrandDashboardFilters,
) {
  const window = resolveDashboardRequestWindow(filters);
  const keyedFilters = { ...filters, ...window };
  const qs = new URLSearchParams({ mode: "my-brand", dateRange: filters.dateRange });
  appendDateWindow(qs, window);
  if (filters.platforms.length > 0) qs.set("platforms", filters.platforms.join(","));
  if (filters.accounts.length > 0) qs.set("accounts", filters.accounts.join(","));
  if (filters.products.length > 0) qs.set("products", filters.products.join(","));

  return queryOptions<MyBrandChartResult | null>({
    queryKey: dashboardKeys.myBrandChart(tenantId ?? "", keyedFilters),
    queryFn: async ({ signal }) => {
      const { data, correlationId } = await authFetch<{ charts: MyBrandKpiCharts }>(
        `/dashboard/my-brand-chart?${qs.toString()}`,
        { signal },
      );
      if (!data) return { status: "pending", correlationId: correlationId! };
      return { status: "ready", charts: data.charts };
    },
    enabled: !!tenantId,
    // Same caching posture as the summary: batch-refreshed data, so refetch only
    // when the filter queryKey changes (first load / filter change).
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: 3,
  });
}

function withResolvedWindow<T extends MyBrandDashboardFilters | CompetitorDashboardFilters>(
  filters: T,
): T & DashboardDateWindow {
  return { ...filters, ...resolveDashboardRequestWindow(filters) };
}

function buildCompetitorQueryString(filters: CompetitorDashboardFilters): URLSearchParams {
  const window = resolveDashboardRequestWindow(filters);
  const qs = new URLSearchParams({ mode: "competitors", dateRange: filters.dateRange });
  appendDateWindow(qs, window);
  if (filters.myIndustry) qs.set("myIndustry", filters.myIndustry);
  if (filters.myBrand) qs.set("myBrand", filters.myBrand);
  if (filters.industryProducts.length > 0) {
    qs.set("industryProducts", filters.industryProducts.join(","));
  }
  if (filters.competitorsIndustry) qs.set("competitorsIndustry", filters.competitorsIndustry);
  if (filters.mainCompetitor) qs.set("mainCompetitor", filters.mainCompetitor);
  if (filters.benchmark.length > 0) qs.set("benchmark", filters.benchmark.join(","));
  // Always send platforms (even empty): the API defaults a *missing* param to
  // ['meta'], so an empty string is how the UI expresses "all platforms" and
  // reaches resolveSourceFilter([])'s 'All' path. Omitting it would silently
  // fall back to Meta-only when the user clears every platform chip.
  qs.set("platforms", filters.platforms.join(","));
  return qs;
}

export function dashboardCompetitorChartQueryOptions(
  tenantId: string | null,
  filters: CompetitorDashboardFilters,
) {
  const keyedFilters = withResolvedWindow(filters);
  const qs = buildCompetitorQueryString(keyedFilters);

  return queryOptions<CompetitorChartResult | null>({
    queryKey: dashboardKeys.competitorChart(tenantId ?? "", keyedFilters),
    queryFn: async ({ signal }) => {
      const { data, correlationId } = await authFetch<{ charts: CompetitorKpiCharts }>(
        `/dashboard/competitor-chart?${qs.toString()}`,
        { signal },
      );
      if (!data) return { status: "pending", correlationId: correlationId! };
      return { status: "ready", charts: data.charts };
    },
    enabled: !!tenantId,
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: 3,
  });
}

export function dashboardCompetitorSummaryQueryOptions(
  tenantId: string | null,
  filters: CompetitorDashboardFilters,
) {
  const keyedFilters = withResolvedWindow(filters);
  const qs = buildCompetitorQueryString(keyedFilters);

  return queryOptions<CompetitorSummaryResult | null>({
    queryKey: dashboardKeys.competitorSummary(tenantId ?? "", keyedFilters),
    queryFn: async ({ signal }) => {
      const { data, correlationId } = await authFetch<{ kpiCards: CompetitorKpiCards }>(
        `/dashboard/summary?${qs.toString()}`,
        { signal },
      );
      if (!data) return { status: "pending", correlationId: correlationId! };
      return { status: "ready", kpiCards: data.kpiCards };
    },
    enabled: !!tenantId,
    // See my-brand summary above — no background refresh; refetch only on
    // filter (queryKey) change or first load.
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: 3,
  });
}

/** Drilldown page defaults, so a row and the Drilldown page it links to share a cache entry. */
const DRILLDOWN_DEFAULT_PAGE_FILTERS: Omit<
  DrilldownPageFiltersInput,
  "startDate" | "endDate" | "sortMode" | "sortDirection"
> = {
  objectType: [],
  effectiveStatus: [],
  audienceSegment: [],
  device: [],
  segment1: "Hash",
  segment2: "All",
  segment3: "All",
  advancedFilters: [],
};

/** First page of Drilldown creatives for one competitor creative row. */
export function dashboardCreativesQueryOptions(
  tenantId: string | null,
  filters: CompetitorFiltersInput,
  request: DashboardCreativesRequest,
) {
  const qs = buildDrilldownQs(
    { ...filters, dateRange: request.dateRange },
    {
      ...DRILLDOWN_DEFAULT_PAGE_FILTERS,
      startDate: request.startDate,
      endDate: request.endDate,
      sortMode: request.sortMode,
      sortDirection: request.sortDirection,
    },
    0,
  );

  return queryOptions<DashboardCreativesResult | null>({
    queryKey: dashboardKeys.competitorCreatives(tenantId ?? "", filters, request),
    queryFn: async ({ signal }) => {
      const { data, correlationId } = await authFetch<{ section: DrilldownQuerySection | null }>(
        `/dashboard/creatives?${qs}`,
        { signal },
      );
      if (!data) return { status: "pending", correlationId: correlationId! };
      return { status: "ready", section: data.section };
    },
    enabled: !!tenantId,
    // Same posture as the summary: batch-refreshed data, refetch only on queryKey change.
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: 3,
  });
}

export function dashboardStatusQueryOptions(
  correlationId: string | null,
  pollStartedAt: number,
) {
  return queryOptions<DashboardStatusData | null>({
    queryKey: dashboardKeys.status(correlationId ?? ""),
    queryFn: async ({ signal }) => {
      const { data } = await authFetch<DashboardStatusData>(
        `/dashboard/status/${correlationId}`,
        { signal },
      );
      return data;
    },
    enabled: !!correlationId && pollStartedAt > 0,
    // Active polling drives the initial fetch; once COMPLETED/FAILED we must not
    // let a focus/reconnect refetch hit a (possibly expired) correlation and
    // wipe the rendered cards. So the only refetch trigger is this interval.
    staleTime: Infinity,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchInterval: (query) => {
      if (!pollStartedAt || !correlationId) return false;
      const s = query.state.data?.status;
      if (s === "COMPLETED" || s === "FAILED") return false;
      if (Date.now() - pollStartedAt > MAX_POLL_DURATION_MS) return false;
      return POLL_INTERVAL_MS;
    },
  });
}

export function competitorFilterDefaultsQueryOptions(
  tenantId: string | null,
  exploreBasePath: ExploreApiPrefix,
) {
  const tid = tenantId ?? "";
  return queryOptions({
    queryKey: dashboardKeys.competitorFilterDefaults(tid || "__none__", exploreBasePath),
    queryFn: async ({ signal }) => fetchCompetitorFilterDefaults(exploreBasePath, { signal }),
    enabled: !!tid,
    staleTime: 0,
  });
}

export function useBootstrapCompetitorFilterDefaults(exploreBasePath: ExploreApiPrefix) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: BootstrapCompetitorFilterDefaultsPayload) =>
      bootstrapCompetitorFilterDefaults(exploreBasePath, payload),
    onSuccess: (_data, _payload, _context) => {
      void queryClient.invalidateQueries({
        queryKey: [...dashboardKeys.all, "competitor-filter-defaults"],
      });
    },
  });
}
